import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Factory, Target, Award, TrendingUp, ArrowRight } from 'lucide-react';
import { manufacturingProcess } from '../mockData';

const About = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  const coreValues = [
    { icon: Factory, title: 'Quality First', description: 'Every product meets the highest manufacturing standards' },
    { icon: Target, title: 'Customer Focus', description: 'We solve real problems for real people' },
    { icon: Award, title: 'Innovation', description: 'Continuously improving through technology and R&D' },
    { icon: TrendingUp, title: 'Reliability', description: 'Consistent supply and support across India' }
  ];

  const differentiators = [
    { title: "We're Manufacturers, Not Traders", description: "Complete control over quality from raw materials to finished products. No middlemen, no compromises." },
    { title: "Revolutionary Technology", description: "Our 6H hardness acrylic boards and nail-free veneer lamination are industry-leading innovations that solve age-old problems." },
    { title: "No Re-Pressing Promise", description: "Our products never require pressing or re-pressing at manufacturing, installation, or usage stages—saving time and ensuring quality." },
    { title: "Pan-India Reach", description: "Reliable supply chain and support network ensuring timely delivery across all regions of India." }
  ];

  return (
    <div className="min-h-screen bg-[#F7F8F5]">
      {/* Hero Section */}
      <section className="relative bg-[#0A0D0C] pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 noise-overlay pointer-events-none" />
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#004534]/30 to-transparent" />
        
        <div className="container mx-auto px-6 lg:px-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl"
          >
            <p className="label-mono text-[#D4AF37] mb-4">Our Story</p>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 font-['Outfit']">
              About CriLam Decorative Pvt. Ltd.
            </h1>
            <p className="text-xl text-white/70 max-w-2xl">
              A trusted name in premium acrylic laminated boards and interior doors, serving the Indian market with excellence and innovation
            </p>
          </motion.div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <p className="label-mono text-[#D4AF37] mb-4">The Beginning</p>
              <h2 className="text-3xl md:text-4xl font-bold text-[#004534] mb-6 font-['Outfit']">Our Journey</h2>
              <div className="space-y-4 text-[#525956]">
                <p>
                  CriLam Decorative Pvt. Ltd. emerged from a simple observation: the laminate industry was plagued with recurring problems—boards that swelled, yellowed, and required constant re-pressing. Interior professionals and end-users deserved better.
                </p>
                <p>
                  With this vision, we established our in-house manufacturing facility with state-of-the-art technology and a commitment to solving these fundamental problems. Our revolutionary 6H hardness acrylic laminated boards never swell or turn yellow, and eliminate the need for pressing at any stage.
                </p>
                <p>
                  Today, we are proud manufacturers of a complete range of interior solutions including acrylic boards, CNC designer doors, decorative wall panels, mica doors, and our innovative nail-free veneer laminated doors—serving architects, designers, dealers, and customers across India.
                </p>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="overflow-hidden">
                <video
                  className="w-full h-auto"
                  controls
                  autoPlay
                  muted
                  loop
                  playsInline
                >
                  <source src="/crilam-about-video.mp4" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Core Values Section */}
      <section className="py-20 lg:py-28 bg-[#0A0D0C] relative">
        <div className="absolute inset-0 noise-overlay pointer-events-none" />
        <div className="container mx-auto px-6 lg:px-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <p className="label-mono text-[#D4AF37] mb-4">What Drives Us</p>
            <h2 className="text-3xl md:text-4xl font-bold text-white font-['Outfit']">Our Core Values</h2>
          </motion.div>

          <motion.div 
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {coreValues.map((value, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className="p-8 bg-white/[0.03] border border-white/[0.08] text-center hover:bg-white/[0.06] transition-colors group"
              >
                <div className="w-16 h-16 flex items-center justify-center bg-[#004534] mx-auto mb-6 group-hover:bg-[#D4AF37] transition-colors">
                  <value.icon className="h-8 w-8 text-[#D4AF37] group-hover:text-[#004534] transition-colors" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">{value.title}</h3>
                <p className="text-white/60 text-sm">{value.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Manufacturing Excellence */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-16"
          >
            <p className="label-mono text-[#D4AF37] mb-4">Production Process</p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#004534] mb-4 font-['Outfit']">Manufacturing Excellence</h2>
            <p className="text-lg text-[#525956] max-w-2xl">
              Our in-house facility combines advanced technology with skilled craftsmanship
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {manufacturingProcess.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                className="bg-[#F7F8F5] border border-black/5 overflow-hidden group hover:border-[#D4AF37]/30 transition-colors"
              >
                <div className="h-48 overflow-hidden">
                  <img
                    src={step.image}
                    alt={step.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                </div>
                <div className="p-6">
                  <span className="inline-block px-3 py-1.5 bg-[#D4AF37] text-[#0A0D0C] font-bold text-sm mb-3">
                    {step.step}
                  </span>
                  <h3 className="text-lg font-semibold text-[#004534] mb-2">{step.title}</h3>
                  <p className="text-[#525956] text-sm">{step.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* What Makes Us Different */}
      <section className="py-20 lg:py-28 bg-[#F7F8F5]">
        <div className="container mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <p className="label-mono text-[#D4AF37] mb-4">Our Edge</p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#004534] font-['Outfit']">What Makes CriLam Different</h2>
          </motion.div>

          <div className="max-w-4xl mx-auto space-y-4">
            {differentiators.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                className="p-6 bg-white border border-black/5 hover:border-[#D4AF37]/30 transition-colors"
              >
                <h3 className="text-xl font-semibold text-[#004534] mb-3">{item.title}</h3>
                <p className="text-[#525956]">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-28 bg-[#004534] relative overflow-hidden">
        <div className="absolute inset-0 noise-overlay pointer-events-none" />
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#D4AF37]/10 to-transparent" />
        
        <div className="container mx-auto px-6 lg:px-12 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <motion.p 
              className="label-mono text-[#D4AF37] mb-4"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              Get Started
            </motion.p>
            <motion.h2 
              className="text-3xl md:text-4xl font-bold text-white mb-6 font-['Outfit']"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              Ready to Experience the CriLam Difference?
            </motion.h2>
            <motion.p 
              className="text-lg text-white/70 mb-10"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              Contact us to learn more about our products, become a dealer, or request samples
            </motion.p>
            
            <motion.div 
              className="flex flex-wrap justify-center gap-4"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <Link to="/contact">
                <button className="px-8 py-4 bg-[#D4AF37] text-[#0A0D0C] font-medium hover:bg-white transition-colors flex items-center gap-2">
                  Contact Us
                  <ArrowRight className="h-4 w-4" />
                </button>
              </Link>
              <Link to="/dealer">
                <button className="px-8 py-4 border border-white/30 text-white hover:bg-white/10 transition-colors">
                  Become a Dealer
                </button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
